import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Severity, Skel } from '../components/ui'

/**
 * Real-time notification engine (part 76).
 *
 * The page is the operator's view of the three tables: rules decide who hears about an event,
 * digests are the batches the engine builds, deliveries are the attempts to hand a batch to a
 * destination. Every timestamp is rendered in Asia/Bangkok explicitly -- a quiet window means
 * Thai wall-clock time, and a browser in another zone must not make 07:00 look like 00:00.
 */
interface Rule {
  id: string
  name: string
  event: string
  severity_min: string
  roles: string[]
  channel: string
  digest_minutes: number
  quiet_start?: string
  quiet_end?: string
  max_attempts: number
  enabled: boolean
  created_at: string
  updated_at: string
}

interface Digest {
  id: string
  rule_id: string
  rule_name: string
  event: string
  channel: string
  status: string
  window_start: string
  window_end: string
  scheduled_for: string
  quiet_deferred: boolean
  event_count: number
  severity_max: string
  next_delivery_at?: string
  sent_at?: string
  created_at: string
  updated_at: string
}

interface Delivery {
  id: string
  digest_id: string
  rule_id: string
  rule_name: string
  channel: string
  status: string
  attempt: number
  scheduled_at: string
  attempted_at?: string
  next_retry_at?: string
  last_error?: string
  created_at: string
  updated_at: string
}

interface EnqueueResult {
  event_id: string
  accepted: boolean
  duplicate: boolean
  matched_rules: number
  digests: Digest[]
  delivery_count: number
  deferred: boolean
  next_delivery_at?: string
}

interface Meta {
  timezone: string
  events: string[]
  severities: string[]
  roles: string[]
  channels: string[]
  default_digest_minutes: number
  max_digest_minutes: number
  default_max_attempts: number
  retry_schedule_seconds: number[]
  quiet_hours_example: string
}

interface RuleDraft {
  id: string
  name: string
  event: string
  severity_min: string
  roles: string[]
  channel: string
  digest_minutes: string
  quiet_start: string
  quiet_end: string
  max_attempts: string
  enabled: boolean
}

const EVENTS = ['finding.created', 'subscription.renewal', 'case.approved', 'schedule.run', 'whistle.received']
const SEVERITIES = ['low', 'medium', 'high', 'critical']
const ROLES = ['owner', 'admin', 'audit_manager', 'auditor', 'reviewer', 'client_viewer', 'system']
const CHANNELS = ['webhook', 'email', 'line', 'slack']

const EVENT_LABEL: Record<string, string> = {
  'finding.created': 'พบข้อบกพร่องใหม่',
  'subscription.renewal': 'ต่ออายุการสมัคร',
  'case.approved': 'อนุมัติเคส',
  'schedule.run': 'รอบงานตามกำหนด',
  'whistle.received': 'รับแจ้งเบาะแส',
}

const SEVERITY_LABEL: Record<string, string> = {
  low: 'ต่ำ', medium: 'ปานกลาง', high: 'สูง', critical: 'วิกฤต',
}

const ROLE_LABEL: Record<string, string> = {
  owner: 'เจ้าขององค์กร', admin: 'ผู้ดูแลระบบ', audit_manager: 'ผู้จัดการตรวจสอบ',
  auditor: 'ผู้ตรวจสอบ', reviewer: 'ผู้สอบทาน', client_viewer: 'ลูกค้าอ่านอย่างเดียว',
  system: 'ระบบ',
}

const CHANNEL_LABEL: Record<string, string> = {
  webhook: 'เว็บฮุก', email: 'อีเมล', line: 'ไลน์', slack: 'สแล็ก',
}

const DIGEST_STATUS: Record<string, string> = {
  pending: 'รอส่ง', sent: 'ส่งแล้ว', failed: 'ล้มเหลว',
}

const DELIVERY_STATUS: Record<string, string> = {
  queued: 'เข้าคิว', sent: 'ส่งแล้ว', retrying: 'รอส่งใหม่', dead: 'สิ้นสุด',
}

const EMPTY_DRAFT: RuleDraft = {
  id: '',
  name: '',
  event: 'finding.created',
  severity_min: 'high',
  roles: [],
  channel: 'email',
  digest_minutes: '15',
  quiet_start: '',
  quiet_end: '',
  max_attempts: '5',
  enabled: true,
}

const BKK_FMT = new Intl.DateTimeFormat('th-TH', {
  timeZone: 'Asia/Bangkok',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

function bkk(iso?: string | null): string {
  if (!iso) return '—'
  return BKK_FMT.format(new Date(iso))
}

function shortId(id: string): string {
  return id.slice(0, 8)
}

function secondsLabel(seconds: number): string {
  if (seconds >= 3600) return `${Math.round(seconds / 3600)} ชั่วโมง`
  if (seconds >= 60) return `${Math.round(seconds / 60)} นาที`
  return `${seconds} วินาที`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function NotifyEngine() {
  const { me } = useAuth()
  const canWrite = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()

  const [formOpen, setFormOpen] = useState(false)
  const [draft, setDraft] = useState<RuleDraft>(EMPTY_DRAFT)
  const [formError, setFormError] = useState('')
  const [notice, setNotice] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [selectedDigest, setSelectedDigest] = useState<string>('')

  const [eventKind, setEventKind] = useState('finding.created')
  const [eventSeverity, setEventSeverity] = useState('high')
  const [eventRole, setEventRole] = useState('system')
  const [dedupeKey, setDedupeKey] = useState('')
  const [lastResult, setLastResult] = useState<EnqueueResult | null>(null)
  const [eventError, setEventError] = useState('')

  const meta = useQuery({
    queryKey: ['notify-engine-meta'],
    queryFn: () => api<Meta>('/v1/notify-engine/meta'),
    staleTime: Infinity,
  })

  const rules = useQuery({
    queryKey: ['notify-engine-rules'],
    queryFn: () => api<Rule[]>('/v1/notify-engine/rules'),
  })

  const digests = useQuery({
    queryKey: ['notify-engine-digests'],
    queryFn: () => api<Digest[]>('/v1/notify-engine/digests?limit=100'),
    // A digest becomes due while the operator watches; a short poll keeps the next delivery
    // column honest without a manual refresh.
    refetchInterval: 15_000,
    staleTime: 5_000,
  })

  const deliveries = useQuery({
    queryKey: ['notify-engine-deliveries', selectedDigest],
    queryFn: () =>
      selectedDigest
        ? api<Delivery[]>(`/v1/notify-engine/digests/${encodeURIComponent(selectedDigest)}/deliveries?limit=100`)
        : api<Delivery[]>('/v1/notify-engine/deliveries?limit=100'),
    refetchInterval: 15_000,
    staleTime: 5_000,
  })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      draft.id
        ? apiWrite<{ rule: Rule }>(`/v1/notify-engine/rules/${encodeURIComponent(draft.id)}`, 'PATCH', body)
        : apiWrite<{ rule: Rule }>('/v1/notify-engine/rules', 'POST', body),
    onSuccess: () => {
      setFormOpen(false)
      setDraft(EMPTY_DRAFT)
      setFormError('')
      setNotice(draft.id ? 'บันทึกการแก้ไขกฎแล้ว' : 'สร้างกฎแจ้งเตือนแล้ว')
      void qc.invalidateQueries({ queryKey: ['notify-engine-rules'] })
    },
    onError: (err) => setFormError(errorText(err, 'บันทึกกฎไม่สำเร็จ')),
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiWrite<{ status: string }>(`/v1/notify-engine/rules/${encodeURIComponent(id)}`, 'DELETE'),
    onSuccess: () => {
      setConfirmDelete(null)
      setNotice('ลบกฎแล้ว ประวัติชุดและการส่งของกฎนี้ถูกลบตาม')
      void qc.invalidateQueries({ queryKey: ['notify-engine-rules'] })
      void qc.invalidateQueries({ queryKey: ['notify-engine-digests'] })
      void qc.invalidateQueries({ queryKey: ['notify-engine-deliveries'] })
    },
    onError: (err) => setFormError(errorText(err, 'ลบกฎไม่สำเร็จ')),
  })

  const enqueue = useMutation({
    mutationFn: () =>
      apiWrite<EnqueueResult>('/v1/notify-engine/events', 'POST', {
        kind: eventKind,
        severity: eventSeverity,
        role: eventRole,
        dedupe_key: dedupeKey.trim(),
      }),
    onSuccess: (result) => {
      setLastResult(result)
      setEventError('')
      setDedupeKey('')
      void qc.invalidateQueries({ queryKey: ['notify-engine-digests'] })
      void qc.invalidateQueries({ queryKey: ['notify-engine-deliveries'] })
    },
    onError: (err) => {
      setLastResult(null)
      setEventError(errorText(err, 'ส่งเหตุการณ์เข้าเอนจินไม่สำเร็จ'))
    },
  })

  const rows = rules.data ?? []
  const digestRows = digests.data ?? []
  const deliveryRows = deliveries.data ?? []
  const pending = digestRows.filter((d) => d.status === 'pending').length
  const deferred = digestRows.filter((d) => d.status === 'pending' && d.quiet_deferred).length
  const retry = meta.data?.retry_schedule_seconds ?? [30, 60, 120, 240]

  function startCreate() {
    if (formOpen) {
      setFormOpen(false)
      return
    }
    setDraft(EMPTY_DRAFT)
    setFormError('')
    setFormOpen(true)
  }

  function startEdit(rule: Rule) {
    setDraft({
      id: rule.id,
      name: rule.name,
      event: rule.event,
      severity_min: rule.severity_min,
      roles: rule.roles ?? [],
      channel: rule.channel,
      digest_minutes: String(rule.digest_minutes),
      quiet_start: rule.quiet_start ?? '',
      quiet_end: rule.quiet_end ?? '',
      max_attempts: String(rule.max_attempts),
      enabled: rule.enabled,
    })
    setFormError('')
    setFormOpen(true)
  }

  function toggleRole(role: string) {
    setDraft((current) =>
      current.roles.includes(role)
        ? { ...current, roles: current.roles.filter((r) => r !== role) }
        : { ...current, roles: [...current.roles, role] },
    )
  }

  function submitRule() {
    setFormError('')
    const digest = Number(draft.digest_minutes)
    const attempts = Number(draft.max_attempts)
    if (!Number.isInteger(digest) || digest < 0 || digest > (meta.data?.max_digest_minutes ?? 1440)) {
      setFormError('ช่วงเวลารวมชุดต้องเป็นจำนวนเต็มระหว่าง 0 ถึง 1440 นาที (0 = ส่งทันที)')
      return
    }
    if (!Number.isInteger(attempts) || attempts < 1 || attempts > 10) {
      setFormError('จำนวนครั้งที่ลองใหม่ต้องอยู่ระหว่าง 1 ถึง 10')
      return
    }
    save.mutate({
      name: draft.name.trim(),
      event: draft.event,
      severity_min: draft.severity_min,
      roles: draft.roles,
      channel: draft.channel,
      digest_minutes: digest,
      quiet_start: draft.quiet_start,
      quiet_end: draft.quiet_end,
      max_attempts: attempts,
      enabled: draft.enabled,
    })
  }

  return (
    <>
      <PageHead
        title="ศูนย์แจ้งเตือนเรียลไทม์"
        sub="รวมเหตุการณ์เป็นชุดตามช่วงเวลา · เงียบตามเวลาไทย · ส่งตามความรุนแรง ชนิดเหตุการณ์ และบทบาท"
        actions={canWrite ? (
          <button className="btn btn-primary" onClick={startCreate}>
            {formOpen ? 'ปิดฟอร์ม' : 'เพิ่มกฎแจ้งเตือน'}
          </button>
        ) : undefined}
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{rows.length} กฎ</span>
        <span className="chip">{pending} ชุดรอส่ง</span>
        <span className="chip" style={deferred > 0 ? { color: 'var(--color-amber)' } : undefined}>
          {deferred} ชุดเลื่อนเพราะเวลางานเงียบ
        </span>
        <span className="chip">เวลาไทย (Asia/Bangkok)</span>
        <span className="chip">ลองใหม่ {retry.map((s) => secondsLabel(s)).join(' · ')}</span>
      </div>

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {formOpen ? (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="card-label">{draft.id ? `แก้ไขกฎ · ${draft.name}` : 'เพิ่มกฎแจ้งเตือนใหม่'}</div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-name">ชื่อกฎ</label>
              <input id="ne-name" className="input" value={draft.name} maxLength={120}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="แจ้งเตือนข้อบกพร่องระดับสูง" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-event">ชนิดเหตุการณ์</label>
              <select id="ne-event" className="select" style={{ width: '100%' }} value={draft.event}
                onChange={(e) => setDraft({ ...draft, event: e.target.value })}>
                {EVENTS.map((event) => (
                  <option key={event} value={event}>{EVENT_LABEL[event] ?? event}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-severity">ระดับความรุนแรงขั้นต่ำ</label>
              <select id="ne-severity" className="select" style={{ width: '100%' }} value={draft.severity_min}
                onChange={(e) => setDraft({ ...draft, severity_min: e.target.value })}>
                {SEVERITIES.map((severity) => (
                  <option key={severity} value={severity}>{SEVERITY_LABEL[severity] ?? severity}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-channel">ช่องทางส่ง</label>
              <select id="ne-channel" className="select" style={{ width: '100%' }} value={draft.channel}
                onChange={(e) => setDraft({ ...draft, channel: e.target.value })}>
                {CHANNELS.map((channel) => (
                  <option key={channel} value={channel}>{CHANNEL_LABEL[channel] ?? channel}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-digest">รวมชุดภายใน (นาที)</label>
              <input id="ne-digest" className="input mono" inputMode="numeric" value={draft.digest_minutes}
                onChange={(e) => setDraft({ ...draft, digest_minutes: e.target.value.replace(/\D/g, '') })}
                placeholder="15" />
              <div className="dim" style={{ fontSize: 11, marginTop: 5 }}>
                0 = ส่งทันทีทีละเหตุการณ์ · เหตุการณ์ในช่วงเดียวกันรวมเป็นชุดเดียว
              </div>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-attempts">จำนวนครั้งที่ลองใหม่</label>
              <input id="ne-attempts" className="input mono" inputMode="numeric" value={draft.max_attempts}
                onChange={(e) => setDraft({ ...draft, max_attempts: e.target.value.replace(/\D/g, '') })}
                placeholder="5" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-quiet-start">เวลาเริ่มงานเงียบ (Asia/Bangkok)</label>
              <input id="ne-quiet-start" className="input mono" type="time" value={draft.quiet_start}
                onChange={(e) => setDraft({ ...draft, quiet_start: e.target.value })} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-quiet-end">เวลาสิ้นสุดงานเงียบ</label>
              <input id="ne-quiet-end" className="input mono" type="time" value={draft.quiet_end}
                onChange={(e) => setDraft({ ...draft, quiet_end: e.target.value })} />
              <div className="dim" style={{ fontSize: 11, marginTop: 5 }}>
                เว้นทั้งสองช่องเพื่อไม่ใช้เวลางานเงียบ · รองรับช่วงข้ามคืน เช่น 22:00-07:00
              </div>
            </div>
            <div className="field" style={{ marginTop: 0, gridColumn: '1 / -1' }}>
              <label>บทบาทผู้รับ</label>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                {ROLES.map((role) => (
                  <button key={role} type="button" className={`chip ${draft.roles.includes(role) ? 'chip-on' : ''}`}
                    aria-pressed={draft.roles.includes(role)} onClick={() => toggleRole(role)}>
                    {ROLE_LABEL[role] ?? role}
                  </button>
                ))}
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                ไม่เลือกบทบาทใดเลย = ทุกบทบาท รวมถึงเหตุการณ์ที่ระบบเป็นผู้สร้าง
              </div>
            </div>
            <div className="field" style={{ marginTop: 0, gridColumn: '1 / -1' }}>
              <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={draft.enabled}
                  onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
                เปิดใช้งานกฎนี้
              </label>
            </div>
          </div>
          {formError ? <div className="form-error">{formError}</div> : null}
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary" disabled={save.isPending || draft.name.trim() === ''}
              onClick={submitRule}>
              {save.isPending ? 'กำลังบันทึก…' : 'บันทึกกฎ'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              กฎที่ปิดใช้งานจะไม่ถูกตรวจตอนรับเหตุการณ์ แต่ยังมองเห็นได้ในรายการ
            </span>
          </div>
        </div>
      ) : null}

      {formError && !formOpen ? <div className="form-error" style={{ marginBottom: 16 }}>{formError}</div> : null}

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">ทดสอบเส้นทาง</div>
            <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
              บันทึกเหตุการณ์เข้าเอนจินจริง · ส่งสามครั้งภายในช่วงเวลาเดียวกันเพื่อดูว่าทั้งสามรวมเป็นชุดเดียว
            </div>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-ev-kind">เหตุการณ์</label>
              <select id="ne-ev-kind" className="select" value={eventKind}
                onChange={(e) => setEventKind(e.target.value)}>
                {EVENTS.map((event) => (
                  <option key={event} value={event}>{EVENT_LABEL[event] ?? event}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-ev-sev">ความรุนแรง</label>
              <select id="ne-ev-sev" className="select" value={eventSeverity}
                onChange={(e) => setEventSeverity(e.target.value)}>
                {SEVERITIES.map((severity) => (
                  <option key={severity} value={severity}>{SEVERITY_LABEL[severity] ?? severity}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-ev-role">บทบาทผู้ก่อเหตุ</label>
              <select id="ne-ev-role" className="select" value={eventRole}
                onChange={(e) => setEventRole(e.target.value)}>
                {ROLES.map((role) => (
                  <option key={role} value={role}>{ROLE_LABEL[role] ?? role}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ne-ev-key">คีย์กันซ้ำ (ไม่บังคับ)</label>
              <input id="ne-ev-key" className="input mono" value={dedupeKey} maxLength={200}
                onChange={(e) => setDedupeKey(e.target.value)}
                placeholder="finding:2026-0042" style={{ minWidth: 190 }} />
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" disabled={!canWrite || enqueue.isPending} onClick={() => enqueue.mutate()}>
            {enqueue.isPending ? 'กำลังบันทึก…' : 'บันทึกเหตุการณ์'}
          </button>
          <span className="dim" style={{ fontSize: 11.5 }}>
            คีย์กันซ้ำว่าง = ใช้รหัสเหตุการณ์อัตโนมัติ ทำให้แต่ละครั้งนับแยก
          </span>
        </div>
        {eventError ? <div className="form-error" style={{ marginTop: 12 }}>{eventError}</div> : null}

        {lastResult ? (
          <div
            className="card card-pad"
            role="status"
            style={{ marginTop: 14, background: 'var(--color-wash)' }}
          >
            {lastResult.matched_rules === 0 ? (
              <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8 }}>
                ไม่มีกฎที่ตรงกับเหตุการณ์นี้ · ตรวจชนิดเหตุการณ์ ระดับความรุนแรง และบทบาทของกฎ
              </div>
            ) : lastResult.duplicate ? (
              <div style={{ fontSize: 12.5, lineHeight: 1.8 }}>
                เหตุการณ์นี้มีคีย์ซ้ำในชุดเดิม จึงไม่ถูกนับเพิ่ม · ชุด {shortId(lastResult.digests[0]?.id ?? '')} มี{' '}
                {lastResult.digests[0]?.event_count ?? 0} เหตุการณ์
              </div>
            ) : (
              <div style={{ fontSize: 12.5, lineHeight: 1.9 }}>
                <div>
                  ยอมรับเหตุการณ์ · ตรงกับ {lastResult.matched_rules} กฎ · สร้าง {lastResult.delivery_count} การส่ง
                </div>
                <div className="dim">
                  {lastResult.digests.map((digest) => (
                    <span key={digest.id} style={{ marginRight: 12 }}>
                      ชุด {shortId(digest.id)} · {digest.event_count} เหตุการณ์ · {digest.rule_name}
                    </span>
                  ))}
                </div>
                {lastResult.deferred ? (
                  <div style={{ color: 'var(--color-amber)' }}>
                    เลื่อนเพราะเวลางานเงียบ · ส่งถัดไป {bkk(lastResult.next_delivery_at)}
                  </div>
                ) : (
                  <div className="dim">ส่งถัดไป {bkk(lastResult.next_delivery_at)}</div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="card-label">กฎที่ประกาศไว้</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            กฎที่ตรงกันทั้งหมดจะได้รับเหตุการณ์ · ไม่ใช่เพียงกฎแรก
          </span>
        </div>
        {rules.isLoading ? <Skel h={200} /> : rules.isError ? (
          <div className="form-error">{errorText(rules.error, 'โหลดกฎแจ้งเตือนไม่สำเร็จ')}</div>
        ) : rows.length === 0 ? (
          <Empty title="ยังไม่มีกฎแจ้งเตือน" body="เพิ่มกฎข้อแรกเพื่อกำหนดว่าเหตุการณ์ใดควรไปถึงใคร และรวมเป็นชุดบ่อยเพียงใด" />
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th>กฎ</th>
                  <th>เส้นทาง</th>
                  <th>การรวมชุด</th>
                  <th>เวลางานเงียบ</th>
                  <th>สถานะ</th>
                  <th style={{ width: 130 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <div>{rule.name}</div>
                      <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>
                        {rule.event} · {CHANNEL_LABEL[rule.channel] ?? rule.channel}
                      </div>
                    </td>
                    <td style={{ fontSize: 11.5 }}>
                      <div className="row" style={{ gap: 8 }}>
                        <Severity value={rule.severity_min} />
                        <span className="dim">ขึ้นไป</span>
                      </div>
                      <div className="dim" style={{ marginTop: 4 }}>
                        {rule.roles.length === 0 ? 'ทุกบทบาท' : rule.roles.map((r) => ROLE_LABEL[r] ?? r).join(' · ')}
                      </div>
                    </td>
                    <td style={{ fontSize: 11.5 }}>
                      {rule.digest_minutes === 0 ? 'ส่งทันที' : `ทุก ${rule.digest_minutes} นาที`}
                      <div className="dim" style={{ marginTop: 4 }}>ลองใหม่สูงสุด {rule.max_attempts} ครั้ง</div>
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {rule.quiet_start && rule.quiet_end ? `${rule.quiet_start}-${rule.quiet_end}` : '—'}
                    </td>
                    <td>
                      <span className="chip" style={rule.enabled ? { color: 'var(--color-emerald-soft)' } : { color: 'var(--color-ivory-dim)' }}>
                        {rule.enabled ? 'เปิด' : 'ปิด'}
                      </span>
                    </td>
                    <td>
                      {canWrite ? (
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(rule)}>แก้ไข</button>
                          {confirmDelete === rule.id ? (
                            <>
                              <button className="btn btn-sm" style={{ color: 'var(--color-crimson)' }}
                                disabled={remove.isPending} onClick={() => remove.mutate(rule.id)}>
                                {remove.isPending ? 'กำลังลบ…' : 'ยืนยัน'}
                              </button>
                              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(null)}>ยกเลิก</button>
                            </>
                          ) : (
                            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(rule.id)}>ลบ</button>
                          )}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="card-label">ชุดที่สร้างขึ้น</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            หนึ่งชุด = หนึ่งการส่ง · เวลาทั้งหมดเป็นเวลาไทย
          </span>
        </div>
        {digests.isLoading ? <Skel h={200} /> : digests.isError ? (
          <div className="form-error">{errorText(digests.error, 'โหลดชุดแจ้งเตือนไม่สำเร็จ')}</div>
        ) : digestRows.length === 0 ? (
          <Empty title="ยังไม่มีชุดแจ้งเตือน" body="เมื่อมีเหตุการณ์ตรงกับกฎ ชุดแรกจะปรากฏที่นี่พร้อมเวลาส่งถัดไป" />
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>ชุด</th>
                  <th>กฎ</th>
                  <th style={{ width: 90 }}>จำนวน</th>
                  <th>ความรุนแรง</th>
                  <th>ปิดหน้าต่าง</th>
                  <th>ส่งถัดไป</th>
                  <th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {digestRows.map((digest) => (
                  <tr key={digest.id} style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedDigest(digest.id === selectedDigest ? '' : digest.id)}>
                    <td className="mono" style={{ fontSize: 11.5 }}>{shortId(digest.id)}</td>
                    <td style={{ fontSize: 11.5 }}>
                      <div>{digest.rule_name}</div>
                      <div className="dim mono" style={{ marginTop: 3 }}>{digest.event}</div>
                    </td>
                    <td className="tnum">{digest.event_count}</td>
                    <td><Severity value={digest.severity_max} /></td>
                    <td className="dim" style={{ fontSize: 11 }}>{bkk(digest.window_end)}</td>
                    <td style={{ fontSize: 11.5 }}>
                      {digest.quiet_deferred ? (
                        <span className="chip" style={{ color: 'var(--color-amber)' }}>เลื่อน · เวลางานเงียบ</span>
                      ) : null}
                      <div className="mono" style={{ marginTop: digest.quiet_deferred ? 5 : 0 }}>
                        {digest.status === 'pending'
                          ? bkk(digest.next_delivery_at)
                          : digest.status === 'sent'
                            ? `ส่งแล้ว ${bkk(digest.sent_at)}`
                            : 'หยุดหลังลองครบ'}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="chip" style={
                        digest.status === 'sent' ? { color: 'var(--color-emerald-soft)' }
                          : digest.status === 'failed' ? { color: 'var(--color-crimson)' }
                            : undefined
                      }>
                        {DIGEST_STATUS[digest.status] ?? digest.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad">
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="card-label">
            การส่ง{selectedDigest ? ` · ชุด ${shortId(selectedDigest)}` : ' · ล่าสุดทั้งหมด'}
          </span>
          {selectedDigest ? (
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setSelectedDigest('')}>
              แสดงทั้งหมด
            </button>
          ) : null}
        </div>
        {deliveries.isLoading ? <Skel h={160} /> : deliveries.isError ? (
          <div className="form-error">{errorText(deliveries.error, 'โหลดรายการส่งไม่สำเร็จ')}</div>
        ) : deliveryRows.length === 0 ? (
          <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8 }}>
            ยังไม่มีการส่ง · การส่งจะถูกสร้างพร้อมชุดแรกของแต่ละกฎ
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>ชุด</th>
                  <th>กฎ</th>
                  <th style={{ width: 90 }}>ช่องทาง</th>
                  <th style={{ width: 80 }}>ครั้งที่</th>
                  <th>กำหนดส่ง</th>
                  <th>ลองใหม่เมื่อ</th>
                  <th style={{ width: 100 }}>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {deliveryRows.map((delivery) => (
                  <tr key={delivery.id}>
                    <td className="mono" style={{ fontSize: 11.5 }}>{shortId(delivery.digest_id)}</td>
                    <td style={{ fontSize: 11.5 }}>{delivery.rule_name}</td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{CHANNEL_LABEL[delivery.channel] ?? delivery.channel}</td>
                    <td className="tnum">{delivery.attempt}</td>
                    <td className="dim" style={{ fontSize: 11 }}>{bkk(delivery.scheduled_at)}</td>
                    <td className="dim" style={{ fontSize: 11 }}>
                      {delivery.next_retry_at ? bkk(delivery.next_retry_at) : '—'}
                      {delivery.last_error ? (
                        <div className="mono" style={{ marginTop: 3, fontSize: 10.5 }}>{delivery.last_error}</div>
                      ) : null}
                    </td>
                    <td>
                      <span className="chip" style={
                        delivery.status === 'sent' ? { color: 'var(--color-emerald-soft)' }
                          : delivery.status === 'dead' ? { color: 'var(--color-crimson)' }
                            : delivery.status === 'retrying' ? { color: 'var(--color-amber)' }
                              : undefined
                      }>
                        {DELIVERY_STATUS[delivery.status] ?? delivery.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="dim" style={{ fontSize: 11, marginTop: 10 }}>
          เพดานการลองใหม่: {retry.map((s) => secondsLabel(s)).join(' · ')} ตามลำดับ · หลังครบจำนวนครั้ง สถานะจะเป็นสิ้นสุด
        </div>
      </div>
    </>
  )
}
